// components/transactions/PrivateTransactionsModal.jsx

import { useState, useEffect } from "react";
import { toast } from "sonner";
import useSelectedDomain from "../../../service/Hooks/useSelectedDomain";
import { getPrivateTransactionsSummary } from "../../../service/api/TransfertTitre/PrivateTransactionService";
import { formatAmountInThousands, formatDate } from "../../../service/utils/formatters";


const PrivateTransactionsModal = ({ show, onClose }) => {
  const selectedDomain = useSelectedDomain();
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5; // Pagination côté React comme demandé

  // Charger les données lorsque le modal s'ouvre
  useEffect(() => {
    if (show && selectedDomain?.id) {
      loadSummaryData();
    }
  }, [show, selectedDomain]);

  const loadSummaryData = async () => {
    try {
      setLoading(true);
      const response = await getPrivateTransactionsSummary(selectedDomain.id);
      
      if (response.success) {
        setSummaryData(response.data);
      } else {
        toast.error("Erreur lors du chargement des données");
      }
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Impossible de charger le résumé des transactions");
    } finally {
      setLoading(false);
    }
  };

  // Calculer les données paginées pour les topSecurities
  const paginatedSecurities = summaryData?.topSecurities?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  ) || [];

  const totalPages = Math.ceil(
    (summaryData?.topSecurities?.length || 0) / itemsPerPage
  );


  if (!show) return null;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div 
        className="modal-dialog modal-custom modal-dialog-centered modal-dialog-scrollable"
      >
        <div className="modal-content border-0 shadow-lg">
          {/* En-tête du modal */}
          <div className="modal-header bg-light text-white">
            <div className="d-flex align-items-center w-100">
              <div className="me-3">
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 8H4C2.9 8 2 8.9 2 10V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V10C22 8.9 21.1 8 20 8Z" fill="primary"/>
                  <path d="M12 12C13.1 12 14 11.1 14 10C14 8.9 13.1 8 12 8C10.9 8 10 8.9 10 10C10 11.1 10.9 12 12 12Z" fill="#2278c9ff"/>
                  <path d="M4 8H20C21.1 8 22 8.9 22 10V10H2V10C2 8.9 2.9 8 4 8Z" fill="#2278c9ff"/>
                  <path d="M9 16H7C6.45 16 6 15.55 6 15C6 14.45 6.45 14 7 14H9C9.55 14 10 14.45 10 15C10 15.55 9.55 16 9 16Z" fill="primary"/>
                </svg>
              </div>
              <div className="flex-grow-1">
                <h5 className="modal-title mb-0 fw-bold">
                  Détails des Transactions Privées
                </h5>
                <small className="text-black">
                  Domaine: {selectedDomain?.name || "Non spécifié"}
                </small>
              </div>
            </div>
            <button 
              type="button" 
              className="btn-close btn-close-primary" 
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>

          {/* Corps du modal */}
          <div className="modal-body p-0">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="mt-3 text-muted">Chargement des données...</p>
              </div>
            ) : summaryData ? (
              <div className="p-4">
                {/* Statistiques principales */}
                <div className="row mb-4">
                  <div className="col-md-3">
                    <div className="card border-0 bg-warning bg-opacity-10 h-100">
                      <div className="card-body text-center">
                        <h6 className="text-warning fw-semibold">Transactions Totales</h6>
                        <h2 className="fw-bold">{summaryData.totalTransactions}</h2>
                        <small className="text-muted">Opérations</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card border-0 bg-success bg-opacity-10 h-100">
                      <div className="card-body text-center">
                        <h6 className="text-success fw-semibold">Valeur Totale</h6>
                        <h2 className="fw-bold">{formatAmountInThousands(summaryData.totalValue)}</h2>
                        <small className="text-muted">Valeur cumulée</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card border-0 bg-info bg-opacity-10 h-100">
                      <div className="card-body text-center">
                        <h6 className="text-info fw-semibold">Quantité Totale</h6>
                        <h2 className="fw-bold">{summaryData.totalQuantity.toLocaleString('fr-FR')}</h2>
                        <small className="text-muted">Titres échangés</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card border-0 bg-secondary bg-opacity-10 h-100">
                      <div className="card-body text-center">
                        <h6 className="text-secondary fw-semibold">Titres Uniques</h6>
                        <h2 className="fw-bold">{summaryData.uniqueSecurities}</h2>
                        <small className="text-muted">Différents titres</small>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Top Securities avec pagination */}
                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-header bg-light">
                    <h6 className="mb-0 fw-bold">
                      Top Sécurités
                      <span className="badge bg-opacity-10 border border-opacity-25 text-primary ms-2">
                        {summaryData.topSecurities?.length || 0}
                      </span>
                    </h6>
                  </div>
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-hover table-striped mb-0">
                        <thead className="table-light">
                          <tr>
                            <th width="40%">Nom du Titre</th>
                            <th width="30%" className="text-center">Nombre de Transactions</th>
                            <th width="30%" className="text-end">Valeur Totale</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedSecurities.map((security, index) => (
                            <tr key={index}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <span className="badge bg-success bg-opacity-10 text-success me-2">
                                    {index + 1 + (currentPage - 1) * itemsPerPage}
                                  </span>
                                  <span className="fw-semibold">{security.securityName}</span>
                                </div>
                              </td>
                              <td className="text-center">
                                <span className="badge bg-warning bg-opacity-10 border border-opacity-25  text-warning">
                                  {security.transactionCount} transactions
                                </span>
                              </td>
                              <td className="text-end fw-bold text-success">
                                {formatAmountInThousands(security.totalValue)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="p-3 border-top">
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted">
                            Page {currentPage} sur {totalPages}
                          </small>
                          <div className="btn-group">
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                              disabled={currentPage === 1}
                            >
                              <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                <path fillRule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"/>
                              </svg>
                            </button>
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                              disabled={currentPage === totalPages}
                            >
                              <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Répartition par type de transaction */}
                <div className="row">
                  <div className="col-md-12">
                    <div className="card border-0 shadow-sm">
                      <div className="card-header bg-light">
                        <h6 className="mb-0 fw-bold">Répartition par Type</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          {Object.entries(summaryData.transactionsByType || {}).map(([type, count], index) => (
                            <div key={index} className="col-md-4 mb-3">
                              <div className="d-flex align-items-center p-3 border rounded">
                                <div className="me-3">
                                  <div className="rounded-5 bg-success bg-opacity-10 text-success p-2">
                                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                                      <path d="M4.285 9.567a.5.5 0 0 1 .683.183A3.498 3.498 0 0 0 8 11.5a3.498 3.498 0 0 0 3.032-1.75.5.5 0 1 1 .866.5A4.498 4.498 0 0 1 8 12.5a4.498 4.498 0 0 1-3.898-2.25.5.5 0 0 1 .183-.683zM7 6.5C7 7.328 6.552 8 6 8s-1-.672-1-1.5S5.448 5 6 5s1 .672 1 1.5zm4 0c0 .828-.448 1.5-1 1.5s-1-.672-1-1.5S9.448 5 10 5s1 .672 1 1.5z"/>
                                    </svg>
                                  </div>
                                </div>
                                <div className="flex-grow-1">
                                  <h6 className="mb-0 fw-semibold">{type}</h6>
                                  <small className="text-muted">{count} transactions</small>
                                </div>
                                <div className="text-end">
                                  <span className="badge bg-opacity-10 border border-opacity-25 text-primary shadow-hover">{count}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Métadonnées */}
                {summaryData.metadata && (
                  <div className="mt-4 text-end">
                    <small className="text-muted">
                      Dernière mise à jour: {formatDate(summaryData.metadata.generatedAt)}
                    </small>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-5">
                <div className="text-muted">
                  <svg width="48" height="48" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                    <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z"/>
                  </svg>
                  <p className="mt-3">Aucune donnée disponible</p>
                </div>
              </div>
            )}
          </div>

          {/* Pied du modal */}
          <div className="modal-footer border-top">
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={onClose}
            >
              Fermer
            </button>
            <button 
              type="button" 
              className="btn btn-primary"
              onClick={() => {
                // Action supplémentaire si nécessaire
                toast.success("Données exportées avec succès");
              }}
            >
              <svg width="16" height="16" fill="currentColor" className="me-2" viewBox="0 0 16 16">
                <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
              </svg>
              Exporter
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivateTransactionsModal;